import { formatCurrency } from "../lib/formatCurrency";

/**
 * Horizontal bar chart for "Spend by X" breakdowns.
 *
 * Props:
 *  - title: section title
 *  - icon: optional Phosphor icon component
 *  - rows: [{ name, count, amount, key }]   // `key` used by onRowClick
 *  - onRowClick: (row) => void              // optional, makes rows clickable
 *  - emptyText
 *  - testId
 */
export function SpendBarChart({ title, icon: Icon, rows, onRowClick, emptyText = "No spend yet", testId, amountClassName = "text-zinc-900", barClassName }) {
  const max = rows.length > 0 ? Math.max(...rows.map((r) => r.amount || 0), 1) : 1;

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={14} weight="duotone" className="text-zinc-500" />}
        <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-300 italic py-4 text-center">{emptyText}</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r, i) => {
            const pct = max > 0 ? Math.max(2, (r.amount / max) * 100) : 0;
            const clickable = Boolean(onRowClick);
            const Wrap = clickable ? "button" : "div";
            return (
              <Wrap
                key={r.key || r.name || i}
                onClick={clickable ? () => onRowClick(r) : undefined}
                data-testid={testId ? `${testId}-row-${i}` : undefined}
                className={`w-full text-left group ${clickable ? "cursor-pointer" : ""}`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className={`text-xs font-medium text-zinc-700 truncate ${clickable ? "group-hover:text-[#002FA7]" : ""}`} title={r.name}>
                    {r.name}
                  </span>
                  <span className={`text-xs font-mono font-bold ${amountClassName} flex-shrink-0`}>
                    {formatCurrency(r.amount)}
                    <span className="text-[10px] font-normal text-zinc-400 ml-1.5">×{r.count}</span>
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                  <div
                    className={`h-full ${barClassName || (clickable ? "bg-[#002FA7] group-hover:bg-[#002280]" : "bg-zinc-700")} transition-all duration-300`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </Wrap>
            );
          })}
        </div>
      )}
    </div>
  );
}
